import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { SessionBoxInfo } from 'components';
import classNames from 'classnames';
import {
  ParticipantFieldType,
  useCountries,
  useParticipantFields,
  useRecruitment,
} from '@app/api';
import { getCriterionValuesTextArray } from '../../../../EditSession/components/Criteria';

const useStyles = createUseStyles((theme: Theme) => ({
  largeTargetCard: {
    gridColumn: '1 / span 2',
    '@media (max-width: 1600px)': {
      gridColumn: 1,
    },
  },
  targetText: {
    whiteSpace: 'pre-wrap',
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,
    ...theme.fonts.caption,
    marginBottom: theme.marginBase * 2,
  },
  categories: {
    ...theme.fonts.caption,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
  },
  category: {
    flex: 1,
    minWidth: 150,
  },
  categoryTitle: {
    ...theme.fonts.label,
    marginBottom: theme.marginBase,
  },
  fieldLabel: {
    ...theme.fonts.label,
  },
  fieldValue: {
    ...theme.fonts.caption,
    paddingLeft: theme.marginBase,
  },
  importantFieldLabel: {
    color: theme.colors.redAccessible,
  },
}));

interface Props {
  className?: string;
  recruitmentId?: string;
}

export const TargetCard = ({ className, recruitmentId }: Props) => {
  const classes = useStyles();
  const { data: recruitment } = useRecruitment(recruitmentId);

  const { data: ppFields } = useParticipantFields();
  const { data: countries } = useCountries();

  const criteriaByCategory = useMemo(() => {
    if (!recruitment?.criteria || !ppFields) {
      return {};
    }
    return recruitment.criteria.reduce(
      (acc, criterion) => {
        const field = ppFields.find((f) => f.name === criterion.fieldRef);
        if (!field) {
          return acc;
        }
        if (!acc[field.category]) {
          acc[field.category] = [];
        }

        acc[field.category].push({
          name: field.name,
          label: field.range?.label || field.label,
          type: field.type,
          values: getCriterionValuesTextArray(
            criterion,
            field.name === 'countryKey'
              ? {
                  ...field,
                  choices: countries?.map(
                    ({ key, name }: { key: string; name: string }) => ({
                      label: name,
                      value: key,
                    }),
                  ),
                }
              : field,
          ),
        });

        return acc;
      },
      {} as Record<
        string,
        Array<{
          name: string;
          label: string;
          type: ParticipantFieldType;
          values?: string[];
        }>
      >,
    );
  }, [recruitment, ppFields, countries]);

  const categories = useMemo(
    () => Object.keys(criteriaByCategory),
    [criteriaByCategory],
  );

  return (
    <SessionBoxInfo
      color="blue"
      title="Target"
      icon="ri-cursor-fill"
      className={classNames(className, {
        [classes.largeTargetCard]: !!recruitment?.targetInformation,
      })}
    >
      {recruitment?.targetInformation && (
        <p className={classes.targetText}>{recruitment.targetInformation}</p>
      )}
      <div className={classes.categories}>
        {categories.map((category) => (
          <div key={category} className={classes.category}>
            <h3 className={classes.categoryTitle}>{category}</h3>
            {criteriaByCategory[category].map((field) => (
              <div key={field.name}>
                <h4
                  className={classNames(classes.fieldLabel, {
                    [classes.importantFieldLabel]: [
                      ParticipantFieldType.GEOLOCATION,
                      ParticipantFieldType.SHORT_TEXT,
                      ParticipantFieldType.LONG_TEXT,
                    ].includes(field.type),
                  })}
                >
                  {field.label}
                </h4>
                <p className={classes.fieldValue}>{field.values?.join(', ')}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </SessionBoxInfo>
  );
};
